import { Skeleton, SkeletonGroup } from '@/components/play'
import { cn } from '@/lib/utils'

/** The Play skeleton. New code can import it from `@/components/play` directly. */
export { Skeleton, SkeletonGroup }

/** Small skeleton for an action or connection already in progress. Inside a button, use the Play Button's `loading` instead. */
export function InlineSkeleton({ label = 'Working', className }: { label?: string; className?: string }) {
  return (
    <span role="status" aria-label={label} aria-busy="true" title={label} className={cn('sb-inline-skeleton h-4 w-4', className)}>
      <span className="sb-skeleton" aria-hidden="true" />
      <span className="sb-skeleton" aria-hidden="true" />
      <span className="sb-skeleton" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  )
}

type ContentSkeletonProps = {
  label: string
  variant?: 'form' | 'list' | 'cards' | 'media' | 'viewer' | 'qr'
  className?: string
  count?: number
}

function SkeletonRows({ count }: { count: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="flex items-center gap-4 py-2">
          <Skeleton className="h-12 w-12" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Match the geometry of the pending content so the page keeps its shape. */
export function ContentSkeleton({ label, variant = 'form', className, count = 3 }: ContentSkeletonProps) {
  return (
    <SkeletonGroup label={label} className={cn('w-full', className)}>
      <div className="space-y-6" aria-hidden="true">
        {variant === 'qr' ? (
          <div className="flex flex-col items-center gap-4">
            <Skeleton shape="media" className="aspect-square w-64 max-w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton shape="button" className="w-full" />
          </div>
        ) : variant === 'media' ? (
          <Skeleton shape="media" />
        ) : variant === 'viewer' ? (
          <>
            <Skeleton className="h-8 w-48 max-w-full" />
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
              <Skeleton shape="media" />
              <SkeletonRows count={3} />
            </div>
          </>
        ) : variant === 'cards' ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: count }, (_, index) => (
              <div key={index} className="space-y-4">
                <Skeleton shape="media" />
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : variant === 'list' ? (
          <SkeletonRows count={count} />
        ) : (
          <>
            <Skeleton shape="circle" className="w-12" />
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton shape="field" />
            <Skeleton shape="field" />
            <Skeleton shape="button" className="w-full" />
          </>
        )}
      </div>
    </SkeletonGroup>
  )
}
